import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './STLFinderCard.css';

interface STLModel {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  downloadUrl: string;
  source: string;
  license: string;
  author: string;
  tags?: string[];
  difficulty?: string;
  printTime?: string;
  material?: string;
}

interface STLSearchResponse {
  status: string;
  search_query: string;
  models: STLModel[];
  error?: string;
}

const STLFinderCard: React.FC = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<STLModel[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Spanish to English translation dictionary for 3D printing terms
  const spanishToEnglish: { [key: string]: string } = {
    // Objects and items
    'soporte': 'stand holder support',
    'porta': 'holder stand',
    'teléfono': 'phone',
    'telefono': 'phone',
    'móvil': 'mobile phone',
    'movil': 'mobile phone',
    'celular': 'phone mobile',
    'coche': 'car',
    'auto': 'car',
    'automóvil': 'car automobile',
    'casa': 'house',
    'hogar': 'home',
    'mesa': 'table',
    'silla': 'chair',
    'organizador': 'organizer',
    'organizadora': 'organizer',
    'caja': 'box',
    'cajón': 'drawer box',
    'cajon': 'drawer box',
    'recipiente': 'container',
    'contenedor': 'container',
    'florero': 'vase',
    'jarrón': 'vase',
    'jarron': 'vase',
    'lámpara': 'lamp',
    'lampara': 'lamp',
    'luz': 'light',
    'gancho': 'hook',
    'colgador': 'hanger hook',
    'clip': 'clip',
    'pinza': 'clip clamp',
    'herramienta': 'tool',
    'llave': 'key wrench',
    'destornillador': 'screwdriver',
    'martillo': 'hammer',
    'tornillo': 'screw',
    'tuerca': 'nut',
    'engranaje': 'gear',
    'rueda': 'wheel',
    'pieza': 'part piece',
    'repuesto': 'replacement part spare',
    
    // Electronics and tech
    'cable': 'cable wire',
    'cables': 'cables wires',
    'cargador': 'charger',
    'batería': 'battery',
    'bateria': 'battery',
    'auriculares': 'headphones',
    'audífonos': 'headphones',
    'audifonos': 'headphones',
    'micrófono': 'microphone',
    'microfono': 'microphone',
    'cámara': 'camera',
    'camara': 'camera',
    'computadora': 'computer',
    'ordenador': 'computer',
    'ratón': 'mouse',
    'raton': 'mouse',
    'teclado': 'keyboard',
    'monitor': 'monitor screen',
    'pantalla': 'screen',
    
    // Toys and games
    'juguete': 'toy',
    'juguetes': 'toys',
    'figura': 'figure figurine',
    'figuras': 'figures figurines',
    'muñeco': 'doll figure',
    'muñeca': 'doll',
    'robot': 'robot',
    'dragón': 'dragon',
    'dragon': 'dragon',
    'dinosaurio': 'dinosaur',
    'animal': 'animal',
    'animales': 'animals',
    'perro': 'dog',
    'gato': 'cat',
    'pájaro': 'bird',
    'pajaro': 'bird',
    'pez': 'fish',
    
    // Kitchen and household
    'vaso': 'cup glass',
    'taza': 'mug cup',
    'plato': 'plate',
    'cubiertos': 'cutlery',
    'cuchillo': 'knife',
    'tenedor': 'fork',
    'cuchara': 'spoon',
    'botella': 'bottle',
    'tapa': 'lid cap cover',
    'tapón': 'cap plug',
    'tapon': 'cap plug',
    
    // Descriptive words
    'pequeño': 'small',
    'pequeña': 'small',
    'grande': 'big large',
    'mediano': 'medium',
    'mediana': 'medium',
    'alto': 'tall high',
    'alta': 'tall high',
    'baja': 'short low',
    'fuerte': 'strong',
    'resistente': 'resistant strong durable',
    'flexible': 'flexible',
    'rígido': 'rigid',
    'rigido': 'rigid',
    'suave': 'smooth soft',
    'áspero': 'rough',
    'aspero': 'rough',
    'decorativo': 'decorative',
    'decorativa': 'decorative',
    'funcional': 'functional',
    'útil': 'useful',
    'util': 'useful',
    'práctico': 'practical',
    'practico': 'practical',
    
    // Actions and functions
    'montaje': 'mount mounting',
    'instalación': 'installation mount',
    'instalacion': 'installation mount',
    'sujetar': 'hold',
    'colgar': 'hang',
    'fijar': 'fix attach',
    'ajustable': 'adjustable',
    'giratorio': 'rotating',
    'plegable': 'foldable',
    'desmontable': 'removable',
    
    // Common prepositions and connectors
    'para': 'for',
    'de': 'of',
    'con': 'with',
    'sin': 'without',
    'sobre': 'on over',
    'dentro': 'inside',
    'fuera': 'outside',
    'encima': 'above on top',
    'debajo': 'below under',
    'junto': 'together',
    'separado': 'separate',
    
    // Places and locations
    'escritorio': 'desk',
    'oficina': 'office',
    'dormitorio': 'bedroom',
    'baño': 'bathroom',
    'cocina': 'kitchen',
    'sala': 'living room',
    'pared': 'wall',
    'suelo': 'floor',
    'techo': 'ceiling',
    'ventana': 'window',
    'puerta': 'door',
    'exterior': 'outdoor outside',
    'interior': 'indoor inside',
    'jardín': 'garden',
    'jardin': 'garden',
    'garaje': 'garage',
    'taller': 'workshop',
    
    // Materials (for context)
    'plástico': 'plastic',
    'plastico': 'plastic',
    'metal': 'metal',
    'madera': 'wood',
    'vidrio': 'glass',
    'goma': 'rubber',
    'silicona': 'silicone',
    'tela': 'fabric',
    'cuero': 'leather',
    'papel': 'paper'
  };

  // Function to detect if text is likely Spanish
  const isSpanishText = (text: string): boolean => {
    const spanishIndicators = [
      // Common Spanish words
      'para', 'con', 'por', 'sin', 'como', 'muy', 'mas', 'más', 'pero', 'que', 'qué',
      'donde', 'dónde', 'cuando', 'cuándo', 'porque', 'también', 'bien', 'mal',
      'grande', 'pequeño', 'bueno', 'malo', 'nuevo', 'viejo', 'rápido', 'lento',
      // Spanish-specific characters
      'ñ', 'á', 'é', 'í', 'ó', 'ú', '¿', '¡'
    ];
    
    const lowerText = text.toLowerCase();
    
    // Check for Spanish-specific characters
    if (spanishIndicators.some(indicator => lowerText.includes(indicator))) {
      return true;
    }
    
    // Check for words in our dictionary
    const words = lowerText.split(/\s+/);
    const spanishWordCount = words.filter(word => 
      spanishToEnglish[word.replace(/[.,!?;:]/g, '')]
    ).length;
    
    // If more than 30% of words are in our Spanish dictionary, consider it Spanish
    return words.length > 0 && spanishWordCount / words.length > 0.3;
  };

  // Function to translate Spanish to English using free translation API
  const translateToEnglish = async (text: string): Promise<string> => {
    try {
      // First check if it's likely Spanish
      if (!isSpanishText(text)) {
        return text; // Return original if not Spanish
      }

      // Try using LibreTranslate (free translation API)
      const response = await fetch('https://libretranslate.com/translate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          q: text,
          source: 'es',
          target: 'en',
          format: 'text'
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.translatedText || text;
      }
    } catch (error) {
      console.log('Translation API failed, using fallback dictionary:', error);
    }

    // Fallback to dictionary method if API fails
    return translateSpanishToEnglishFallback(text);
  };

  // Fallback dictionary method (simplified version of the previous function)
  const translateSpanishToEnglishFallback = (query: string): string => {
    const words = query.toLowerCase().split(/\s+/);
    const translatedWords = words.map(word => {
      const cleanWord = word.replace(/[.,!?;:]/g, '');
      return spanishToEnglish[cleanWord] || word;
    });
    
    return translatedWords.join(' ');
  };
  
  const handleSearch = async () => {
    if (!searchQuery.trim() || isSearching) return;
    
    setIsSearching(true);
    setHasSearched(true);
    setSearchError(null);
    
    try {
      // Determine the base URL for API calls
      const baseUrl = window.location.hostname === 'localhost' 
        ? 'http://localhost:8001' 
        : '';
      
      // Translate Spanish terms to English for better search results
      const originalQuery = searchQuery.trim();
      const translatedQuery = await translateToEnglish(originalQuery);
      
      // Use translated query for search
      const searchTerm = translatedQuery;
      
      const response = await fetch(`${baseUrl}/api/v1/stl-search/search?q=${encodeURIComponent(searchTerm)}&max_results=15`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: STLSearchResponse = await response.json();
      
      if (data.status === 'success') {
        setSearchResults(data.models);
        setSearchError(null);
      } else {
        setSearchError(data.error || t('stlFinder.errors.searchFailed'));
        setSearchResults([]);
      }
      
    } catch (error) {
      console.error('Search failed:', error);
      setSearchError(error instanceof Error ? error.message : t('stlFinder.errors.searchFailedConnection'));
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };
  
  const handleDownload = (model: STLModel) => {
    // Check if it's a placeholder URL
    if (model.downloadUrl.includes('example.com')) {
      alert(t('stlFinder.alerts.demoResult', { source: model.source }));
      return;
    }
    
    // For specific model URLs, open in new tab
    // Users will see the actual model page with download options
    const newWindow = window.open(model.downloadUrl, '_blank');
    
    // Provide user feedback
    if (newWindow) {
      // Optional: Show a toast or notification
      console.log(`Opening "${model.title}" on ${model.source}`);
    } else {
      // Handle popup blocker
      alert(t('stlFinder.alerts.popupBlocked', { title: model.title, source: model.source, url: model.downloadUrl }));
    }
  };
  
  const handleNewSearch = () => {
    setHasSearched(false);
    setSearchResults([]);
    setSearchQuery('');
    setSearchError(null);
  };
  
  const handleExampleClick = (exampleQuery: string) => {
    setSearchQuery(exampleQuery);
  };
  
  return (
    <div className="stl-finder-card-no-bg">
      <div className={`stl-finder-content ${hasSearched ? 'with-results' : ''}`}>
        {/* Results Interface - Shows at top when there are results */}
        {hasSearched && (
          <div className="stl-results-container">
            {isSearching ? (
              <div className="searching-indicator">
                <div className="spinner"></div>
                <h3>{t('stlFinder.searching.title', { query: searchQuery })}</h3>
                <p>{t('stlFinder.searching.description')}</p>
              </div>
            ) : searchError ? (
              <div className="search-error">
                <h3>{t('stlFinder.errors.title')}</h3>
                <p>{searchError}</p>
                <button className="retry-search-btn" onClick={handleSearch}>
                  {t('stlFinder.buttons.tryAgain')}
                </button>
              </div>
            ) : searchResults.length === 0 ? (
              <div className="no-results">
                <h3>{t('stlFinder.noResults.title')}</h3>
                <p>{t('stlFinder.noResults.description')}</p>
                <button className="retry-search-btn" onClick={handleNewSearch}>
                  {t('stlFinder.buttons.newSearch')}
                </button>
              </div>
            ) : (
              <div className="stl-results-grid">
                <div className="model-cards-grid">
                  {searchResults.map((model) => (
                    <div key={model.id} className="stl-model-card">
                      <div className="model-thumbnail">
                        <img 
                          src={model.thumbnail} 
                          alt={model.title}
                          onError={(e) => {
                            // Try multiple fallback strategies
                            const img = e.target as HTMLImageElement;
                            
                            // If it's already our placeholder, don't try again
                            if (img.src.includes('placeholder-stl.svg')) {
                              return;
                            }
                            
                            // First fallback: try a generic Thingiverse preview if it's a Thingiverse URL
                            if (model.downloadUrl.includes('thingiverse.com/thing:') && !img.dataset.triedGeneric) {
                              const thingId = model.downloadUrl.match(/thing:(\d+)/)?.[1];
                              if (thingId) {
                                img.dataset.triedGeneric = 'true';
                                img.src = `https://cdn.thingiverse.com/renders/${thingId}/preview.jpg`;
                                return;
                              }
                            }
                            
                            // Final fallback to our placeholder
                            img.src = '/placeholder-stl.svg';
                          }}
                        />
                      </div>
                      
                      <div className="model-info">
                        <div className="model-source-badge">{model.source}</div>
                        <h4 className="model-title">{model.title}</h4>
                        <p className="model-description">{model.description}</p>
                        


                        
                        <button 
                          className="download-model-btn"
                          onClick={() => handleDownload(model)}
                        >
                          {t('stlFinder.buttons.viewOn', { source: model.source })}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Search Interface - Hidden when searching, at bottom when results are shown */}
        {!isSearching && (
          <div className={`stl-search-interface ${hasSearched ? 'with-results' : ''}`}>
            {!hasSearched && (
              <div className="search-prompt">
                <h3>{t('stlFinder.searchPrompt.title')}</h3>
                <p>{t('stlFinder.searchPrompt.description')}</p>
              </div>
            )}
            
            <div className="stl-search-input-container">
              <div className="stl-search-input-wrapper">
                <textarea
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={t('stlFinder.searchInput.placeholder')}
                  className="stl-search-textarea"
                  maxLength={300}
                  rows={2}
                  disabled={isSearching}
                />
                <button 
                  onClick={handleSearch}
                  className={`stl-search-button ${searchQuery.trim() ? 'enabled' : ''}`}
                  disabled={!searchQuery.trim()}
                  title={t('stlFinder.buttons.findModels')}
                >
                  <img src="/right_arrow.svg" alt="Search" style={{width: '16px', height: '16px'}} />
                </button>
              </div>
            </div>
            
            <div className="stl-search-examples">
              <p>
                {t('stlFinder.examples.text')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default STLFinderCard; 